import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"

// Main orchestrator for the form builder UI
export default class extends Controller {
  static targets = ["palette", "sectionsContainer"]
  static values = {
    formId: String,
    createFieldUrl: String,
    createSectionUrl: String
  }

  connect() {
    this.initializePaletteSortable()
    this.initializeSectionsSortable()
    
    // Watch for new sections being added to the DOM
    this.setupMutationObserver()

    // The palette renders into the layout sidebar, outside this controller's
    // element, so it cannot use a form-builder action. It dispatches on window
    // and we listen here.
    this.onPaletteAdd = (event) => this.addFieldOfType(event.detail.fieldType)
    window.addEventListener("field-palette:add", this.onPaletteAdd)

    // The form outline (TASK-267.6) also lives in the layout sidebar — twice,
    // desktop + mobile Menu panel — so its "+ Add Section" dispatches on
    // window too, and runs the same create path as the canvas's bar.
    this.onOutlineAddSection = () => this.addSectionFromOutline()
    window.addEventListener("form-outline:add-section", this.onOutlineAddSection)

    // The row whose settings slideover is open stays marked until it closes.
    this.onSlideoverClosed = () => this.clearSelectedField()
    window.addEventListener("slideover:closed", this.onSlideoverClosed)
  }

  disconnect() {
    if (this.paletteSortable) this.paletteSortable.destroy()
    if (this.sectionsSortable) this.sectionsSortable.destroy()
    if (this.mutationObserver) this.mutationObserver.disconnect()
    window.removeEventListener("field-palette:add", this.onPaletteAdd)
    window.removeEventListener("form-outline:add-section", this.onOutlineAddSection)
    window.removeEventListener("slideover:closed", this.onSlideoverClosed)
  }

  // The section a click-to-add lands in. Defaults to the last section, which is
  // where someone building a form top-to-bottom is working.
  get targetSectionId() {
    if (this.activeSectionId && this.sectionElement(this.activeSectionId)) return this.activeSectionId

    const containers = this.element.querySelectorAll("[data-fields-container]")
    const last = containers[containers.length - 1]
    return last ? last.dataset.sectionId : null
  }

  sectionElement(sectionId) {
    return this.element.querySelector(`[data-fields-container][data-section-id="${CSS.escape(sectionId)}"]`)
  }

  // Adds a field of the given type. With no sections yet it creates "Section 1"
  // first (ensureSection) rather than refusing.
  async addFieldOfType(fieldType) {
    const sectionId = await this.ensureSection()
    if (!sectionId) return

    const count = this.sectionElement(sectionId)?.children.length || 0
    this.createField(sectionId, fieldType, count)
  }

  // The reusable "a section exists" guarantee: resolves with the id of the
  // section a click-to-add should land in, creating one (default title, no
  // rename prompt) only when the form has none. Concurrent callers share one
  // in-flight create, so two quick palette clicks never make two sections.
  async ensureSection() {
    const existing = this.targetSectionId
    if (existing) return existing

    if (!this.creatingSection) {
      this.creatingSection = this.createSection({ autoedit: false })
        .finally(() => { this.creatingSection = null })
    }
    return this.creatingSection
  }

  // POSTs a new section (the server names it "Section N"), renders the stream
  // (autoedit: open its title for renaming) and resolves with the new section id, or null on failure.
  async createSection({ autoedit = true } = {}) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(this.createSectionUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({ form_section: { title: "" }, autoedit })
      })
      if (!response.ok) return null

      const html = await response.text()
      Turbo.renderStreamMessage(html)
      // The stream inserts the section's fields container; its id carries the section id.
      return html.match(/id="section_([0-9a-f-]+)_fields"/)?.[1] || null
    } catch (error) {
      console.error("Error adding section:", error)
      return null
    }
  }

  setupMutationObserver() {
    if (!this.hasSectionsContainerTarget) return
    
    this.mutationObserver = new MutationObserver((mutations) => {
      let shouldReinitialize = false
      
      mutations.forEach((mutation) => {
        if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
          // Check if any added nodes contain fields containers
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              if (node.querySelector("[data-fields-container]") || node.hasAttribute("data-fields-container")) {
                shouldReinitialize = true
              }
            }
          })
        }
      })
      
      if (shouldReinitialize) {
        // Small delay to ensure DOM is fully updated
        setTimeout(() => this.initializeFieldsSortables(), 50)
      }
    })
    
    this.mutationObserver.observe(this.sectionsContainerTarget, {
      childList: true,
      subtree: true
    })
  }

  initializePaletteSortable() {
    // Palette may be in sidebar outside the controller element, so search the whole document
    const palette = document.querySelector('[data-form-builder-target="palette"]')
    if (!palette) return

    this.paletteSortable = Sortable.create(palette, {
      group: {
        name: "fields",
        pull: "clone",
        put: false
      },
      sort: false,
      animation: 150,
      ghostClass: "opacity-50",
      dragClass: "shadow-lg",
      onStart: (evt) => {
        document.body.classList.add("is-dragging")
      },
      onEnd: (evt) => {
        document.body.classList.remove("is-dragging")
      }
    })
  }

  initializeSectionsSortable() {
    if (!this.hasSectionsContainerTarget) return

    // Don't recreate if already initialized
    if (!this.sectionsSortable) {
      this.sectionsSortable = Sortable.create(this.sectionsContainerTarget, {
        group: "sections",
        handle: "[data-section-drag-handle]",
        animation: 150,
        ghostClass: "opacity-50",
        onEnd: (evt) => {
          this.updateSectionPositions()
        }
      })
    }

    // Initialize sortable for each section's fields container
    this.initializeFieldsSortables()
  }

  initializeFieldsSortables() {
    const fieldsContainers = this.element.querySelectorAll("[data-fields-container]")
    
    fieldsContainers.forEach((container) => {
      if (container._sortable) return // Already initialized

      container._sortable = Sortable.create(container, {
        group: "fields",
        handle: "[data-field-drag-handle]",
        animation: 150,
        ghostClass: "opacity-50",
        emptyInsertThreshold: 50,
        onAdd: (evt) => {
          this.handleFieldDrop(evt)
        },
        onEnd: (evt) => {
          if (evt.from === evt.to) {
            this.updateFieldPositions(evt.to)
          }
        }
      })
    })
  }

  handleFieldDrop(evt) {
    const item = evt.item
    const sectionId = evt.to.dataset.sectionId
    const fieldType = item.dataset.fieldType

    // If this is a new field from the palette
    if (fieldType && item.classList.contains("field-type-item")) {
      // Remove the cloned palette item
      item.remove()

      // Create the field via AJAX
      this.createField(sectionId, fieldType, evt.newIndex)
    } else {
      // Field was moved between sections. Sortable moves the row's
      // turbo-frame (the container's direct child); the id is on the row.
      const fieldId = item.dataset.fieldId || item.querySelector("[data-field-id]")?.dataset.fieldId
      if (fieldId) {
        this.moveField(fieldId, sectionId, evt.newIndex)
      }
    }
  }

  async createField(sectionId, fieldType, position) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    
    try {
      const response = await fetch(this.createFieldUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({
          section_id: sectionId,
          field_type: fieldType,
          position: position
        })
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        
        // Re-initialize sortables after new content
        setTimeout(() => this.initializeFieldsSortables(), 100)
      }
    } catch (error) {
      console.error("Error creating field:", error)
    }
  }

  async moveField(fieldId, sectionId, position) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const url = this.createFieldUrlValue.replace(/\/fields$/, `/fields/${fieldId}/move`)
    
    try {
      // Asks for the stream: a cross-section drag changes two section counts
      // and the outline, which the server re-renders by class.
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "text/vnd.turbo-stream.html"
        },
        body: JSON.stringify({
          section_id: sectionId,
          position: position
        })
      })
      if (response.ok) Turbo.renderStreamMessage(await response.text())
    } catch (error) {
      console.error("Error moving field:", error)
    }
  }

  async updateSectionPositions() {
    this.syncOutlineOrder()

    const sections = this.sectionsContainerTarget.querySelectorAll("[data-section-id]")
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    
    sections.forEach(async (section, index) => {
      const sectionId = section.dataset.sectionId
      const url = this.createSectionUrlValue.replace(/\/sections$/, `/sections/${sectionId}/move`)
      
      try {
        await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({ position: index })
        })
      } catch (error) {
        console.error("Error updating section position:", error)
      }
    })
  }

  async updateFieldPositions(container) {
    const fields = container.querySelectorAll("[data-field-id]")
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    
    fields.forEach(async (field, index) => {
      const fieldId = field.dataset.fieldId
      const url = this.createFieldUrlValue.replace(/\/fields$/, `/fields/${fieldId}/move`)
      
      try {
        await fetch(url, {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken
          },
          body: JSON.stringify({ position: index })
        })
      } catch (error) {
        console.error("Error updating field position:", error)
      }
    })
  }

  // "+ Add Section" / "Add first section". The new section arrives with its
  // title in inline-rename mode (see section_title_controller).
  addSection(event) {
    event.preventDefault()
    this.createSection()
  }

  // The outline's "+ Add Section": same create, then bring the new card into
  // view — the outline click may come from far up (or down) the page.
  async addSectionFromOutline() {
    const sectionId = await this.createSection()
    if (!sectionId) return
    document.getElementById(`form_section_${sectionId}`)?.scrollIntoView({ block: "center" })
  }

  // A section drag reorders the canvas client-side; mirror that order into
  // every outline copy (desktop + mobile Menu panel — hence the class/
  // attribute lookups, never ids) and renumber, so the outline never shows
  // the old order until the next reload.
  syncOutlineOrder() {
    const order = Array.from(this.sectionsContainerTarget.querySelectorAll("[data-section-id][data-controller~='form-section']"))
      .map((el) => el.dataset.sectionId)

    document.querySelectorAll(".form-outline-items").forEach((list) => {
      const items = new Map(Array.from(list.querySelectorAll("li[data-outline-section-id]"))
        .map((li) => [li.dataset.outlineSectionId, li]))
      order.forEach((id, index) => {
        const li = items.get(id)
        if (!li) return
        list.appendChild(li)
        const number = li.querySelector("[data-outline-number]")
        if (number) number.textContent = index + 1
      })
    })
  }

  // The row's body opens its settings slideover (the slideover action does
  // that); this marks the row as the one being edited until it closes.
  selectField(event) {
    const row = event.currentTarget.closest("[data-field-row]")
    if (!row) return

    this.clearSelectedField()
    row.classList.remove("border-gray-300", "bg-white")
    row.classList.add("border-blue-600", "bg-blue-50")
    row.dataset.fieldSelected = "true"
  }

  clearSelectedField() {
    document.querySelectorAll("[data-field-row][data-field-selected]").forEach((row) => {
      row.classList.remove("border-blue-600", "bg-blue-50")
      row.classList.add("border-gray-300", "bg-white")
      delete row.dataset.fieldSelected
    })
  }
}
